// * Bootstrap libraries
import './_bootstrap';
import Tooltip from 'bootstrap/js/dist/tooltip';

// * Any other global site-wide JavaScript should be placed below.

((Drupal, once) => {
  const TOGGLE_SELECTOR = '.dropdown > .dropdown-toggle[data-bs-toggle="dropdown"]';
  const OPEN_BEFORE_CLICK_KEY = 'act4allWasOpenBeforeClick';
  const COPY_LINK_SELECTOR = '[data-copy-link]';
  const COPY_LINK_TOOLTIP_TITLE = Drupal.t('Αντιγραφή συνδέσμου');
  const COPY_LINK_TOOLTIP_SUCCESS = Drupal.t('Αντιγράφηκε');
  const LOAD_MORE_SELECTOR = '[data-act4all-load-more]';
  const LOAD_MORE_DEFAULT_BATCH = 18;
  const LOAD_MORE_HIDDEN_CLASS = 'd-none';

  const isToggleOpen = (toggle) => {
    const parentDropdown = toggle.closest('.dropdown');
    const menu = parentDropdown?.querySelector(':scope > .dropdown-menu');

    return (
      toggle.getAttribute('aria-expanded') === 'true' ||
      toggle.classList.contains('show') ||
      parentDropdown?.classList.contains('show') ||
      menu?.classList.contains('show')
    );
  };

  const onDropdownTogglePointerDown = (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR);

    if (!toggle) {
      return;
    }

    toggle.dataset[OPEN_BEFORE_CLICK_KEY] = isToggleOpen(toggle) ? '1' : '0';
  };

  const onDropdownToggleClick = (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR);

    if (!toggle) {
      return;
    }

    const href = toggle.getAttribute('href');
    if (!href || href === '#') {
      return;
    }

    const wasOpenBeforeClick = toggle.dataset[OPEN_BEFORE_CLICK_KEY] === '1';
    delete toggle.dataset[OPEN_BEFORE_CLICK_KEY];

    if (!wasOpenBeforeClick) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.metaKey || event.ctrlKey || toggle.getAttribute('target') === '_blank') {
      window.open(toggle.href, '_blank', 'noopener');
      return;
    }

    window.location.assign(toggle.href);
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const onCopyLinkClick = async (event) => {
    const button = event.currentTarget;
    const link = button.dataset.copyLink;

    if (!link) {
      return;
    }

    try {
      await copyTextToClipboard(link);

      const tooltip = Tooltip.getOrCreateInstance(button, {
        trigger: 'manual',
        placement: 'top',
      });
      const originalTitle = button.getAttribute('data-bs-original-title') || COPY_LINK_TOOLTIP_TITLE;

      button.setAttribute('data-bs-original-title', COPY_LINK_TOOLTIP_SUCCESS);
      tooltip.show();

      window.clearTimeout(button._copyTooltipTimeout);
      button._copyTooltipTimeout = window.setTimeout(() => {
        button.setAttribute('data-bs-original-title', originalTitle);
        tooltip.hide();
      }, 1200);
    } catch (error) {
      // Leave the button functional without surfacing an intrusive error.
      console.error('Unable to copy link to clipboard.', error);
    }
  };

  Drupal.behaviors.act4allDropdownSecondClick = {
    attach(context) {
      once('act4all-dropdown-second-click', '.navbar', context).forEach((navbar) => {
        navbar.addEventListener('pointerdown', onDropdownTogglePointerDown);
        navbar.addEventListener('click', onDropdownToggleClick);
      });
    },
  };

  Drupal.behaviors.act4allCopyLink = {
    attach(context) {
      once('act4all-copy-link', COPY_LINK_SELECTOR, context).forEach((button) => {
        button.addEventListener('click', onCopyLinkClick);
      });
    },
  };

  // Cascades checkbox state from a parent taxonomy term to its children.
  // Applies to any core checkboxes widget rendering a hierarchical
  // vocabulary flat with '-' prefixed labels for depth (field_geo_crete,
  // field_geo_cyprus, and any future field using the same widget).
  Drupal.behaviors.act4allCheckboxHierarchy = {
    attach(context) {
      once('act4all-checkbox-hierarchy', '.form-checkboxes', context).forEach((container) => {
        const items = Array.from(container.querySelectorAll('input[type="checkbox"]')).map((input) => {
          const label = container.querySelector(`label[for="${input.id}"]`);
          const text = label ? label.textContent.trim() : '';
          const depth = (text.match(/^-*/) || [''])[0].length;
          return { input, depth };
        });

        items.forEach((item, index) => {
          item.input.addEventListener('change', () => {
            const { checked } = item.input;
            for (let i = index + 1; i < items.length; i += 1) {
              if (items[i].depth <= item.depth) break;
              items[i].input.checked = checked;
              items[i].input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });

          // Visual indent: swap the leading '-' depth markers for a data
          // attribute the SCSS reads (see _register-form.scss), so the
          // hierarchy is shown as indentation instead of literal dashes.
          if (item.depth > 0) {
            const label = container.querySelector(`label[for="${item.input.id}"]`);
            if (label) {
              label.dataset.depth = item.depth;
              label.textContent = label.textContent.replace(/^-+\s*/, '');
            }
          }
        });
      });
    },
  };

  // Client-side "load more" for the organizations grid (ADR-068).
  //
  // The view renders every result up front (pager: display all items) and
  // views-bootstrap-grid--act4all-search-organizations-group.html.twig emits
  // everything past the first batch with `d-none`. Clicking the button just
  // strips that class off the next batch — no AJAX, no extra requests. Facets
  // AJAX replaces the whole wrapper, so `once` re-initialises on fresh markup.
  const initLoadMore = (wrapper) => {
    const grid = wrapper.querySelector('[data-load-more-grid]');
    const button = wrapper.querySelector('[data-load-more-button]');

    if (!grid || !button) {
      return;
    }

    const remainingOutput = wrapper.querySelector('[data-load-more-remaining]');
    const status = wrapper.querySelector('[data-load-more-status]');
    const batchSize =
      Number.parseInt(wrapper.dataset.loadMoreBatch, 10) || LOAD_MORE_DEFAULT_BATCH;
    const items = Array.from(grid.children);
    const hiddenItems = () =>
      items.filter((item) => item.classList.contains(LOAD_MORE_HIDDEN_CLASS));

    button.addEventListener('click', () => {
      const revealed = hiddenItems().slice(0, batchSize);
      revealed.forEach((item) => item.classList.remove(LOAD_MORE_HIDDEN_CLASS));

      const remaining = hiddenItems().length;

      if (remainingOutput) {
        remainingOutput.textContent = remaining;
      }

      if (status) {
        status.textContent = Drupal.t('Εμφανίζονται @shown από @total ομάδες.', {
          '@shown': items.length - remaining,
          '@total': items.length,
        });
      }

      if (remaining === 0) {
        button.remove();
      }

      // Keyboard and screen reader users land on the first newly revealed card
      // instead of being dropped at the end of the document.
      const [firstRevealed] = revealed;
      if (firstRevealed) {
        firstRevealed.setAttribute('tabindex', '-1');
        firstRevealed.focus({ preventScroll: true });
      }
    });
  };

  Drupal.behaviors.act4allLoadMore = {
    attach(context) {
      once('act4all-load-more', LOAD_MORE_SELECTOR, context).forEach(initLoadMore);
    },
  };

  // Client-side language switcher for the educational-material document list
  // (ADR-081). Each media-file-list render carrying 2+ languages shows one
  // row of "el / en / de / ..." pill buttons (data-media-lang-switcher);
  // items in the sibling list (data-media-lang-list) carry a matching
  // data-lang and start hidden unless they match the server-rendered
  // default. Plain show/hide via classList — no fetch, no re-render, the
  // whole document list for every language is already in the DOM.
  const LANG_SWITCHER_SELECTOR = '[data-media-lang-switcher]';
  const LANG_HIDDEN_CLASS = 'd-none';

  const initMediaLangSwitcher = (switcher) => {
    const list = switcher.parentElement?.querySelector('[data-media-lang-list]');
    if (!list) {
      return;
    }

    const buttons = Array.from(switcher.querySelectorAll('[data-lang-btn]'));
    const items = Array.from(list.querySelectorAll('[data-lang]'));

    const showLang = (code) => {
      items.forEach((item) => {
        item.classList.toggle(LANG_HIDDEN_CLASS, item.dataset.lang !== code);
      });
      buttons.forEach((button) => {
        const isActive = button.dataset.langBtn === code;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => showLang(button.dataset.langBtn));
    });
  };

  Drupal.behaviors.act4allMediaLangSwitcher = {
    attach(context) {
      once('act4all-media-lang-switcher', LANG_SWITCHER_SELECTOR, context).forEach(initMediaLangSwitcher);
    },
  };
})(Drupal, once);
